/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { useWatch, Controller } from "react-hook-form";

import { Icon } from "@fluentui/react/lib/Icon";
import { Label } from "@fluentui/react/lib/Label";
import { Text } from "@fluentui/react/lib/Text";
import { TextField } from "@fluentui/react/lib/TextField";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";

import CharacterCounter from "Components/CharacterCounter/CharacterCounter";
import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import { HookErrorMessage } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage";

import { ITextFieldControllerProps } from "./ITextFieldControllerProps";
import {
  getIconStyles,
  textFieldStyles,
  CounterAndErrorMessageDiv,
} from "./TextFieldController.styles";

/**
 * TextFieldController.
 * @component
 * @param {ITextFieldControllerProps} props The component props.
 * @returns {React.ReactElement} TextFieldController component
 */
const TextFieldController: (props: ITextFieldControllerProps) => React.ReactElement = (
  props: ITextFieldControllerProps
): React.ReactElement => {
  const {
    characterCounterProps,
    characterCountLimit,
    control,
    defaultValue,
    errors,
    hasTooltip,
    inputName,
    isDisabled,
    isMultiline,
    isRequired,
    labelStyles,
    localizedDescriptionDisplayText,
    labelDisplayName,
    placeholderText,
    toolTipDisplayText,
    onChange: onChangeFromProps,
    prefix,
    subText,
    suffix,
    textFieldType,
  } = props;

  const textFieldValue = useWatch({
    control,
    name: inputName,
    defaultValue: defaultValue || "",
  });

  /**
   * Triggers onChangeFromProps if it exists.
   * @param {string} value updated value.
   * @returns {void}
   */
  const handleOnChange = (value?: string): void => {
    if (onChangeFromProps) {
      onChangeFromProps(value);
    }
  };

  return (
    <FlexCol>
      <FlexRow>
        <Label htmlFor={inputName} required={isRequired} styles={labelStyles}>
          {labelDisplayName}
        </Label>
        {hasTooltip && (
          <TooltipHost id={inputName} content={toolTipDisplayText || labelDisplayName}>
            <Icon
              aria-label="information"
              iconName="info"
              role="img"
              styles={getIconStyles(!!isRequired)}
              tabIndex={0}
            />
          </TooltipHost>
        )}
      </FlexRow>
      <FlexRow>
        {localizedDescriptionDisplayText && <Text>{localizedDescriptionDisplayText}</Text>}
        {subText}
      </FlexRow>
      <FlexRow>
        <Controller
          name={inputName}
          control={control}
          render={({ field }): React.ReactElement => {
            return (
              <TextField
                styles={textFieldStyles}
                maxLength={characterCountLimit}
                disabled={isDisabled}
                multiline={isMultiline}
                autoAdjustHeight={isMultiline}
                prefix={prefix}
                suffix={suffix}
                ariaLabel={labelDisplayName}
                onChange={(_, newValue): void => {
                  field.onChange(newValue);
                  handleOnChange(newValue);
                }}
                placeholder={placeholderText}
                type={textFieldType}
                value={textFieldValue}
              />
            );
          }}
        />
      </FlexRow>
      <CounterAndErrorMessageDiv>
        <HookErrorMessage errors={errors} inputName={inputName} />
        {!!characterCounterProps && (
          <CharacterCounter {...characterCounterProps} currentCount={textFieldValue.length} />
        )}
      </CounterAndErrorMessageDiv>
    </FlexCol>
  );
};

export default TextFieldController;
